// Signature slots of the configurable (ROUTE) printout, built from the paper's route
// (sent by the server) and its recorded approvals.

const fullName = (person) =>
  [person && person.firstName, person && person.lastName].filter(Boolean).join(" ");

export const isRoutePrint = (paper, printSettings) =>
  !!printSettings && printSettings.layout === "ROUTE" && !!paper && Array.isArray(paper.route);

/** An info step's lines: what was filled, else its labels with blanks to fill by hand. */
const infoLinesOf = (entry, approval) =>
  approval && approval.infoLines
    ? approval.infoLines
    : (entry.infoLabels || []).map((label) => ({ label, value: null }));

/**
 * { top, bottom }: the top band holds the steps marked TOP (highest order first);
 * the bottom band starts with the creator ("INTOCMIT") and follows the route order.
 * Steps the paper skipped or that are hidden from print are left out. A pending step
 * names who it is for (fallbacks left out); a recorded one, who actually acted.
 * Signatures appear only once the whole paper is approved, as on the classic printout.
 * An info step prints its lines instead of a signature, after the signatures of its band
 * (at the theatre: right of VIZĂ CFP); filled values show as soon as they are recorded.
 */
export const routePrintSlots = (paper, creator) => {
  const approvedPaper = paper.state === 2;
  const approvals = paper.approvals || [];
  const byOrder = {};
  let reached = null;
  approvals.forEach((approval) => {
    if (byOrder[approval.orderNumber] === undefined) {
      byOrder[approval.orderNumber] = approval;
    }
    reached = reached === null ? approval.orderNumber : Math.max(reached, approval.orderNumber);
  });

  const top = [];
  const bottom = [];
  const topInfo = [];
  const bottomInfo = [];
  const used = {};
  paper.route.forEach((entry) => {
    if (entry.type === "RECORDED") {
      return;
    }
    const approval = byOrder[entry.order];
    if (approval) {
      used[entry.order] = true;
    }
    if (entry.printBand === "HIDDEN") {
      return;
    }
    // Recorded: lines if lines were filled; pending: if the step has lines.
    const info = approval ? !!approval.infoLines : (entry.infoLabels || []).length > 0;
    let slot;
    if (approval) {
      slot = {
        key: "order-" + entry.order,
        order: entry.order,
        calling: approval.calling,
        name: fullName(approval),
        signatureLink: approvedPaper && approval.approved ? approval.signatureLink : null,
      };
    } else if (approvedPaper || (reached !== null && entry.order <= reached)) {
      return;
    } else {
      slot = {
        key: "order-" + entry.order,
        order: entry.order,
        calling: entry.calling,
        // Who the step is meant for: the signer or the chiefs, never the fallbacks.
        name: (entry.candidates || []).filter((c) => !c.fallback).map(fullName).join(" / "),
        signatureLink: null,
      };
    }
    if (info) {
      slot.signatureLink = null;
      slot.infoLines = infoLinesOf(entry, approval);
      (entry.printBand === "TOP" ? topInfo : bottomInfo).push(slot);
      return;
    }
    (entry.printBand === "TOP" ? top : bottom).push(slot);
  });
  approvals.forEach((approval) => {
    if (!used[approval.orderNumber]) {
      used[approval.orderNumber] = true;
      const recorded = {
        key: "order-" + approval.orderNumber,
        order: approval.orderNumber,
        calling: approval.calling,
        name: fullName(approval),
        signatureLink: approvedPaper && approval.approved ? approval.signatureLink : null,
      };
      if (approval.infoLines) {
        bottomInfo.push({ ...recorded, signatureLink: null, infoLines: approval.infoLines });
      } else {
        bottom.push(recorded);
      }
    }
  });

  top.sort((a, b) => b.order - a.order);
  bottom.sort((a, b) => a.order - b.order);
  bottom.unshift({
    key: "creator",
    calling: "INTOCMIT",
    name: fullName(creator),
    signatureLink: approvedPaper && creator ? creator.signatureLink : null,
  });
  const routeOrder = (a, b) => a.order - b.order;
  return { top: top.concat(topInfo.sort(routeOrder)), bottom: bottom.concat(bottomInfo.sort(routeOrder)) };
};

/** Splits slots into rows of `size` for the signature grid. */
export const inRows = (slots, size) => {
  const rows = [];
  for (let start = 0; start < slots.length; start += size) {
    rows.push(slots.slice(start, start + size));
  }
  return rows;
};
